import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, OnDestroy, Optional } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  defer,
  firstValueFrom,
  forkJoin,
  from,
  of,
  throwError
} from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import {
  AICreditBalance,
  AppliedCoupon,
  BillingPortalLink,
  BILLING_CURRENCY_CODES,
  CancellationError,
  CancellationInput,
  CancellationPreview,
  CancellationReason,
  ConsumptionEvent,
  CountryCode,
  CurrencyCode,
  EnforcementStatus,
  FeatureUsageBreakdown,
  Invoice,
  PaymentMethod,
  Plan,
  PlanChangeError,
  PlanChangeInput,
  PlanChangePreview,
  ReactivateError,
  Receipt,
  RetentionOffer,
  RetentionOfferError,
  RetentionOfferType,
  Subscription,
  TaxIdError,
  TaxIdInput,
  TaxIdRecord,
  TaxPreview,
  TaxPreviewInput,
  TopUpCheckoutError,
  TopUpHistoryEntry,
  TopUpPack,
  TopUpSuccess
} from '../../models/billing';
import { BillingPermissions } from '../../app-configs/billing/permissions';
import { environment } from '../../environments/environment';
import { normalizePricingModel } from '../../utils/plan-pricing';
import { AuthService } from '../auth.service';
import { LoggerService } from '../logger.service';
import { PermissionService } from '../permission.service';
import { AppSettingsService } from '../app-settings.service';
import { BillingMockService } from '../billing-mock/billing-mock.service';
import {
  CurrentUser,
  IBillingMockService,
  PromotionCodeError,
  ScenarioName,
  UserRole
} from '../billing-mock/billing-mock.interface';

/*
 * Canonical wave ownership map (from T0.3 brief)
 *
 * Prototype doc #02 -> T6: discount admin CRUD + tenant apply/validate
 * Prototype doc #03 -> T1: Stripe Portal delegation (invoices, receipts, payment methods)
 * Prototype doc #04 -> T2: plan change preview + commit
 * Prototype doc #05 -> T3: cancel + reactivate + retention
 * Prototype doc #06 -> T4: dunning + enforcement
 * Prototype doc #07 -> T9: Stripe emails + Solo retention emails
 * Prototype doc #08 -> T8: Stripe Meters + usage detail readouts
 * Prototype doc #09 -> T5: top-up checkout + balance
 * Prototype doc #10 -> T7: tax preview + tax ID + admin override
 */

interface FunctionResponse<TData> {
  data: TData;
}

interface BillingSummaryApiResponse {
  Subscription: ApiSubscription | null;
  AvailablePlans: ApiPlan[];
  AICreditBalance: ApiAICreditBalance | null;
}

interface ApiSubscription {
  Id: string;
  StripeSubscriptionId: string | null;
  TenantId: string;
  Status: string;
  Tier: string;
  BillingPeriod: string;
  Currency: string;
  CountryCode: string;
  Seats: number;
  PricingModel?: string;
  IncludedSeats?: number | null;
  ExtraSeatPriceMinor?: number | null;
  PricePerSeatMinor: number;
  SubtotalMinor: number;
  DiscountMinor: number;
  TaxMinor: number;
  TotalMinor: number;
  AppliedCoupon: ApiAppliedCoupon | null;
  CurrentPeriodStart: string;
  CurrentPeriodEnd: string | null;
  CancelAtPeriodEnd: boolean;
  CanceledAt: string | null;
  PastDueSince: string | null;
  GracePeriodEndsAt: string | null;
}

interface ApiAppliedCoupon {
  PromotionCode: string;
  CouponId: string;
  Name: string;
  DiscountType: 'percent' | 'fixed';
  PercentOff: number | null;
  AmountOffMinor: number | null;
  Duration: 'once' | 'repeating' | 'forever';
  DurationInMonths: number | null;
  AppliedAt: string;
  ExpiresAt: string | null;
}

interface ApiPromotionCodeValidation {
  Valid: boolean;
  Error: PromotionCodeError | null;
  Coupon: ApiAppliedCoupon | null;
}

interface ApiPlan {
  Code: string;
  DisplayName: string;
  ShortDescription: string;
  Features: string[];
  Pricing: ApiPricePoint[];
}

interface ApiPricePoint {
  Currency: string;
  CountryCode: string;
  BillingPeriod: string;
  PerSeatMinor: number;
  AICreditsPerSeat: number;
  BasicTurnsPerSeat: number;
  PricingModel?: string;
  IncludedSeats?: number | null;
  ExtraSeatMonthlyMinor?: number | null;
  StripePriceId: string | null;
}

interface ApiAICreditBalance {
  TenantId: string;
  BillingCycleStart: string;
  BillingCycleEnd: string;
  AllocatedCredits: number;
  UsedCredits: number;
  ReservedCredits: number;
  TopUpBalance: number;
  RemainingCredits: number;
  BasicTurnsAllocated: number;
  BasicTurnsUsed: number;
}

interface ApiRetentionOffer {
  RetentionOfferId?: string;
  Type: RetentionOfferType;
  Headline: string;
  Body: string;
  CtaLabel: string;
  Payload: Record<string, unknown>;
}

interface ApiCancellationPreview {
  Reason: CancellationReason;
  EndOfPeriod: string;
  IsAnnual: boolean;
  IsPastDue: boolean;
  TopUpBalanceAtCancel: number;
  CycleCreditsRemaining: number;
  Offers: ApiRetentionOffer[];
  RetentionOffersRateLimited: boolean;
  AccessEnds?: 'PeriodEnd' | 'Now';
}

interface ApiPlanChangeResolvedInput {
  TargetTier: string;
  TargetSeats: number;
  TargetBillingPeriod: string;
}

interface ApiPlanChangeWarning {
  Code: string;
  UsedCredits?: number | null;
  NewAllocation?: number | null;
  ActiveUsers?: number | null;
  NewSeats?: number | null;
  FirstAnnualChargeMinor?: number | null;
  NextRenewalAt?: string | null;
  AccountCreditAppliedMinor?: number | null;
}

interface ApiPlanChangeProrationLine {
  Description: string;
  PeriodStart: string;
  PeriodEnd: string;
  AmountMinor: number;
}

interface ApiPlanChangePreview {
  PreviewToken: string;
  CurrentSubtotalMinor: number;
  NewSubtotalMinor: number;
  ProrationLines: ApiPlanChangeProrationLine[];
  SubtotalMinor: number;
  DiscountMinor: number;
  TotalMinor: number;
  AccountCreditAppliedMinor: number;
  EffectiveDate: string;
  ChangeTiming?: 'immediate' | 'renewal';
  AppliedRule?: string;
  NextRegularChargeAt: string;
  NextRegularChargeMinor: number;
  Currency: string;
  Warnings: ApiPlanChangeWarning[];
  ResolvedInput: ApiPlanChangeResolvedInput;
}

interface EnforcementStatusApiResponse {
  Level: 'none' | 'past_due' | 'restricted';
  Severity?: 'info' | 'warning' | 'urgent' | 'blocked' | null;
  DaysRemaining?: number | null;
  RestrictedFeatures: Array<'ai_chat' | 'create_record' | 'background_jobs'>;
  BannerDismissible: boolean;
  BannerMessage: string;
  CtaUrl: string;
  PastDueSince: string | null;
  GracePeriodEndsAt: string | null;
}

interface ApiTopUpPackListResponse {
  Packs: ApiTopUpPack[];
}

interface ApiTopUpPack {
  Id: string;
  DisplayName: string;
  Credits: number;
  Pricing: ApiTopUpPrice[];
}

interface ApiTopUpPrice {
  Currency: string;
  CountryCode: string;
  AmountMinor: number;
  StripePriceId: string | null;
}

interface ApiTopUpCheckoutResponse {
  Url: string;
  CreditTopUpId: string;
  CheckoutSessionId: string;
}

interface ApiTopUpHistoryListResponse {
  Entries: ApiTopUpHistoryEntry[];
}

interface ApiTopUpHistoryEntry {
  Id: string;
  CreditTopUpId: string;
  PurchasedAt: string;
  PackDisplayName: string;
  Credits: number;
  AmountMinor: number;
  Currency: string;
  Remaining: number;
  Status: string;
}

interface ApiConsumptionListResponse {
  Events: ApiConsumptionEvent[];
}

interface ApiConsumptionEvent {
  Id: string;
  OccurredAt: string;
  FeatureKey: string;
  CreditsConsumed: number;
  ModelTier: string;
  RequestSummary: string;
  BalanceAfter: number;
}

interface ApiUsageBreakdownListResponse {
  Breakdown: ApiFeatureUsageBreakdown[];
}

interface ApiFeatureUsageBreakdown {
  FeatureKey: string;
  DisplayName: string;
  CreditsUsed: number;
  RequestCount: number;
  PercentOfTotal: number;
}

interface CurrentUserApiResponse {
  Id: string;
  CanManageBilling: boolean;
}

interface ApiTaxPreview {
  TaxableAmountMinor: number;
  TaxAmountMinor: number;
  TaxRate: number;
  TaxBasis: 'inclusive' | 'exclusive' | 'reverse_charge';
  Jurisdiction: string;
  Label: string;
  Calculation: 'auto' | 'manual' | 'reverse_charge';
  ReverseChargeNote?: string | null;
  CurrencyCode?: string | null;
}

interface ApiTaxIdRecord {
  Id: string;
  Type: string;
  Value: string;
  CountryCode: string;
  VerificationStatus?: string | null;
  AddedOn?: string | null;
  Valid?: boolean | null;
  ValidationError?: string | null;
  CreatedAt?: string | null;
}

const COUNTRY_CODES: readonly CountryCode[] = ['US', 'NG', 'GH', 'KE', 'ZA', 'GB', 'DE', 'FR', 'IE'];
const BILLING_PERIODS = ['monthly', 'annual'] as const;
const TIER_CODES = ['FOUNDATION', 'PROFESSIONAL', 'BUSINESS'] as const;
const PROMOTION_CODE_ERRORS: readonly PromotionCodeError[] = [
  'PROMOTION_CODE_NOT_FOUND',
  'PROMOTION_CODE_EXPIRED',
  'PROMOTION_CODE_REDEMPTION_LIMIT_REACHED',
  'PROMOTION_CODE_CURRENCY_MISMATCH',
  'PROMOTION_CODE_DEACTIVATED',
  'PROMOTION_CODE_ALREADY_APPLIED'
];
const SUBSCRIPTION_STATUSES = [
  'active',
  'past_due',
  'unpaid',
  'canceled',
  'incomplete',
  'incomplete_expired',
  'paused'
] as const;

@Injectable({ providedIn: 'root' })
export class BillingService implements IBillingMockService, OnDestroy {
  private readonly subscriptionSubject = new BehaviorSubject<Subscription | null>(null);
  private readonly availablePlansSubject = new BehaviorSubject<Plan[]>([]);
  private readonly aiCreditBalanceSubject = new BehaviorSubject<AICreditBalance | null>(null);
  private readonly enforcementStatusSubject = new BehaviorSubject<EnforcementStatus>({
    level: 'none',
    restrictedFeatures: [],
    ctaUrl: '/billing'
  });
  private readonly currentUserSubject = new BehaviorSubject<CurrentUser>({
    id: '',
    canManageBilling: true
  });
  private readonly paymentRecoverySubject = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  readonly paymentRecoveryEvents$: Observable<void>;
  readonly activeScenarioChanges$?: Observable<ScenarioName>;

  private hasLoadedSummary = false;
  private hasLoadedEnforcementStatus = false;
  private hasLoadedCurrentUser = false;
  private inFlightRefresh$: Observable<void> | null = null;
  private inFlightEnforcementRefresh$: Observable<void> | null = null;
  private inFlightCurrentUser$: Observable<void> | null = null;
  private fellBackToMock = false;
  private warnedAboutFallback = false;
  private mockBridgeInitialized = false;
  private paymentRecoveryEventSource: EventSource | null = null;
  private paymentRecoveryEventToken: string | null = null;
  private readonly topUpPacksCache = new Map<CurrencyCode, Observable<TopUpPack[]>>();
  private lastSessionToken: string | null = null;
  private accountViewAllowed: boolean | null = null;
  private accountViewLookup: Promise<boolean> | null = null;
  private accountViewGeneration = 0;

  constructor(
    private readonly http: HttpClient,
    private readonly logger: LoggerService,
    private readonly authService: AuthService,
    private readonly appSettings: AppSettingsService,
    private readonly injector: Injector,
    private readonly permissions: PermissionService,
    @Optional() private readonly billingMockService?: BillingMockService
  ) {
    const mock = this.tryGetMockService();
    this.paymentRecoveryEvents$ = this.paymentRecoverySubject.asObservable();
    this.activeScenarioChanges$ = mock?.activeScenarioChanges$;

    this.authService.authSession$
      .pipe(
        map(session => session.token),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(token => {
        const sessionChanged = token !== this.lastSessionToken;
        this.lastSessionToken = token;

        if (sessionChanged) {
          this.resetSessionCache();
          if (token) {
            void this.lookupAccountView();
          }
        }

        if (this.fellBackToMock) {
          this.disconnectPaymentRecoveryStream();
          return;
        }

        this.connectPaymentRecoveryStream(token);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnectPaymentRecoveryStream();
  }

  getCurrentSubscription(): Observable<Subscription | null> {
    return this.withSummary(this.subscriptionSubject.asObservable());
  }

  getAvailablePlans(countryCode: CountryCode, currency: CurrencyCode): Observable<Plan[]> {
    return this.withSummary(
      this.availablePlansSubject.asObservable().pipe(
        map(plans =>
          plans
            .map(plan => ({
              ...plan,
              pricing: plan.pricing.filter(
                price => price.countryCode === countryCode && price.currency === currency
              )
            }))
            .filter(plan => plan.pricing.length > 0)
        )
      )
    );
  }

  getAICreditBalance(): Observable<AICreditBalance | null> {
    return this.withSummary(this.aiCreditBalanceSubject.asObservable());
  }

  refresh(): Observable<void> {
    if (!this.hasAuthToken()) {
      this.applyEmptyBillingContext();
      return of(void 0);
    }

    if (this.fellBackToMock) {
      return forkJoin({
        summary: this.populateFromMock(),
        enforcement: this.populateEnforcementFromMock(),
        currentUser: this.populateCurrentUserFromMock()
      }).pipe(map(() => void 0));
    }

    return this.whenAccountViewAllowed().pipe(
      switchMap(allowed => (allowed ? this.refreshAccountBilling() : this.skipAccountBilling()))
    );
  }

  private refreshAccountBilling(): Observable<void> {
    if (this.inFlightRefresh$) {
      return this.inFlightRefresh$;
    }

    this.inFlightRefresh$ = forkJoin({
      summary: this.fetchSummaryData(),
      enforcement: this.enforcementForAccountRefresh()
    }).pipe(
      tap(({ summary, enforcement }) => {
        this.applyBillingSummary(summary);
        const mappedEnforcement = enforcement
          ? this.mapEnforcementStatus(enforcement)
          : this.enforcementStatusSubject.value;
        if (enforcement) {
          this.enforcementStatusSubject.next(mappedEnforcement);
        }
        this.hasLoadedEnforcementStatus = true;

        this.logger.info('BillingService', 'refresh', 'Billing summary loaded', {
          hasSubscription: this.subscriptionSubject.value !== null,
          availablePlanCount: this.availablePlansSubject.value.length,
          hasAICreditBalance: this.aiCreditBalanceSubject.value !== null,
          enforcementLevel: mappedEnforcement.level
        });
      }),
      map(() => void 0),
        catchError(error => {
          if (this.shouldFallbackToMock(error)) {
            this.fellBackToMock = true;
            this.disconnectPaymentRecoveryStream();
            this.ensureMockBridge();
            if (!this.warnedAboutFallback) {
              this.warnedAboutFallback = true;
              console.warn('[BillingService] backend unreachable — falling back to mock service (dev only)');
            }
            this.logger.info(
              'BillingService',
              'refresh',
              'Backend unreachable, falling back to mock service for read streams'
            );
            return forkJoin({
              summary: this.populateFromMock(),
              enforcement: this.populateEnforcementFromMock(),
              currentUser: this.populateCurrentUserFromMock()
            }).pipe(map(() => void 0));
          }

          if (error instanceof HttpErrorResponse && error.status === 401) {
            this.logger.warn(
              'BillingService',
              'refresh',
              'Billing summary request unauthorized; using empty billing context',
              { error }
            );
            this.applyEmptyBillingContext();
            this.hasLoadedSummary = true;
            this.hasLoadedEnforcementStatus = true;
            return of(void 0);
          }

          if (error instanceof HttpErrorResponse && error.status === 403) {
            this.logger.warn(
              'BillingService',
              'refresh',
              'Billing summary request forbidden; using empty billing context',
              { error }
            );
            this.applyEmptyBillingContext();
            this.hasLoadedSummary = true;
            this.hasLoadedEnforcementStatus = true;
            return of(void 0);
          }

          this.logger.error('BillingService', 'refresh', 'Billing summary request failed', { error });
          return throwError(() => error);
        }),
        finalize(() => {
          this.inFlightRefresh$ = null;
        }),
        shareReplay(1)
      );

    return this.inFlightRefresh$;
  }

  listInvoices(_limit?: number): Observable<Invoice[]> {
    return this.notImplemented$('listInvoices', 'T1');
  }

  listReceipts(_limit?: number): Observable<Receipt[]> {
    return this.notImplemented$('listReceipts', 'T1 (delegated to Stripe Portal)');
  }

  createPortalSession(returnUrl: string): Observable<BillingPortalLink> {
    const normalizedReturnUrl = this.toAbsoluteUrl(returnUrl);
    const body = {
      parameters: { ReturnUrl: normalizedReturnUrl },
      pagination: { pageNumber: 1, pageSize: 1 },
      sorting: { sortField: '', sortDirection: 'asc' }
    };
    return this.http
      .post<FunctionResponse<{ Url: string }>>(
        `${this.apiBaseUrl}/api/functions/billing/portal-session/execute`,
        body
      )
      .pipe(
        map(response => ({
          url: response.data.Url,
          expiresAt: '',
          returnUrl: normalizedReturnUrl
        })),
        catchError((err: HttpErrorResponse) => {
          const code = err.error?.messages?.[0]?.code;
          if (code === 'NO_STRIPE_CUSTOMER') {
            return throwError(() => new Error('NO_STRIPE_CUSTOMER'));
          }
          return throwError(() => err);
        })
      );
  }

  getDefaultPaymentMethod(): Observable<PaymentMethod | null> {
    return this.notImplemented$('getDefaultPaymentMethod', 'T1 (delegated to Stripe Portal)');
  }

  validatePromotionCode(code: string): Observable<AppliedCoupon | { error: PromotionCodeError }> {
    return this.http
      .post<FunctionResponse<ApiPromotionCodeValidation>>(
        `${this.apiBaseUrl}/api/functions/billing/promo/validate/execute`,
        this.buildFunctionRequest({ Code: code })
      )
      .pipe(
        map(response => {
          const result = response.data;
          if (!result.Valid || result.Coupon === null) {
            return { error: result.Error ?? 'PROMOTION_CODE_NOT_FOUND' };
          }

          return this.mapAppliedCoupon(result.Coupon)!;
        }),
        catchError((err: HttpErrorResponse) => this.toTypedError<PromotionCodeError>(err, PROMOTION_CODE_ERRORS))
      );
  }

  applyPromotionCode(code: string): Observable<AppliedCoupon | { error: PromotionCodeError }> {
    return this.http
      .post<FunctionResponse<ApiAppliedCoupon>>(
        `${this.apiBaseUrl}/api/functions/billing/promo/apply/execute`,
        this.buildFunctionRequest({ Code: code })
      )
      .pipe(
        switchMap(response => {
          const applied = this.mapAppliedCoupon(response.data);
          if (applied === null) {
            return throwError(() => new Error('BillingService.applyPromotionCode returned no coupon.'));
          }

          return this.refresh().pipe(map(() => applied));
        }),
        catchError((err: HttpErrorResponse) => this.toTypedError<PromotionCodeError>(err, PROMOTION_CODE_ERRORS))
      );
  }

  removeActivePromotionCode(): Observable<void> {
    return this.http
      .post<FunctionResponse<{ Removed: boolean }>>(
        `${this.apiBaseUrl}/api/functions/billing/promo/remove/execute`,
        this.buildFunctionRequest({})
      )
      .pipe(switchMap(() => this.refresh()));
  }

  listTopUpPacks(currency: CurrencyCode): Observable<TopUpPack[]> {
    if (this.fellBackToMock) {
      return this.requireMockService('listTopUpPacks', 'T5 fallback').listTopUpPacks(currency);
    }

    const cached = this.topUpPacksCache.get(currency);
    if (cached) {
      return cached;
    }

    const request$ = this.http
      .post<FunctionResponse<ApiTopUpPackListResponse>>(
        `${this.apiBaseUrl}/api/functions/billing/credits/packs/list/execute`,
        this.buildFunctionRequest({})
      )
      .pipe(
        map(response => response.data.Packs.map(pack => this.mapTopUpPack(pack, currency))),
        catchError(error => {
          if (this.shouldFallbackToMock(error)) {
            this.fellBackToMock = true;
            this.disconnectPaymentRecoveryStream();
            this.ensureMockBridge();
            return this.requireMockService('listTopUpPacks', 'T5 fallback').listTopUpPacks(currency);
          }

          return throwError(() => error);
        }),
        shareReplay(1)
      );

    this.topUpPacksCache.set(currency, request$);
    return request$;
  }

  initiateTopUpCheckout(
    packId: string,
    _currency: CurrencyCode,
    _countryCode: CountryCode
  ): Observable<{ checkoutSessionId: string; checkoutUrl: string } | { error: TopUpCheckoutError }> {
    if (this.fellBackToMock) {
      return this.requireMockService('initiateTopUpCheckout', 'T5 fallback')
        .initiateTopUpCheckout(packId, _currency, _countryCode);
    }

    return this.http
      .post<FunctionResponse<ApiTopUpCheckoutResponse>>(
        `${this.apiBaseUrl}/api/functions/billing/credits/topup/checkout/execute`,
        this.buildFunctionRequest({
          CreditTopUpPackId: packId,
          OriginUrl: this.currentOrigin()
        })
      )
      .pipe(
        map(response => {
          this.storePendingCreditTopUpId(response.data.CreditTopUpId);
          return {
            checkoutSessionId: response.data.CheckoutSessionId,
            checkoutUrl: response.data.Url
          };
        }),
        catchError(error => of({ error: this.mapTopUpCheckoutError(error) }))
      );
  }

  simulateTopUpCompletion(
    _checkoutSessionId: string
  ): Observable<TopUpSuccess | { error: TopUpCheckoutError }> {
    return throwError(() => new Error('BillingService.simulateTopUpCompletion: dev-only — real completion happens via Stripe webhook'));
  }

  listRecentConsumption(limit?: number): Observable<ConsumptionEvent[]> {
    if (this.fellBackToMock) {
      return this.requireMockService('listRecentConsumption', 'T5 fallback').listRecentConsumption(limit);
    }

    return this.http
      .post<FunctionResponse<ApiConsumptionListResponse>>(
        `${this.apiBaseUrl}/api/functions/billing/credits/consumption/list/execute`,
        this.buildFunctionRequest({ Limit: limit ?? null })
      )
      .pipe(
        map(response => response.data.Events.map(event => this.mapConsumptionEvent(event))),
        catchError(error => {
          if (this.shouldFallbackToMock(error)) {
            this.fellBackToMock = true;
            this.disconnectPaymentRecoveryStream();
            this.ensureMockBridge();
            return this.requireMockService('listRecentConsumption', 'T5 fallback').listRecentConsumption(limit);
          }

          return throwError(() => error);
        })
      );
  }

  getFeatureUsageBreakdown(): Observable<FeatureUsageBreakdown[]> {
    if (this.fellBackToMock) {
      return this.requireMockService('getFeatureUsageBreakdown', 'T5 fallback').getFeatureUsageBreakdown();
    }

    return this.http
      .post<FunctionResponse<ApiUsageBreakdownListResponse>>(
        `${this.apiBaseUrl}/api/functions/billing/credits/usage-breakdown/execute`,
        this.buildFunctionRequest({})
      )
      .pipe(
        map(response => response.data.Breakdown.map(row => this.mapFeatureUsageBreakdown(row))),
        catchError(error => {
          if (this.shouldFallbackToMock(error)) {
            this.fellBackToMock = true;
            this.disconnectPaymentRecoveryStream();
            this.ensureMockBridge();
            return this.requireMockService('getFeatureUsageBreakdown', 'T5 fallback').getFeatureUsageBreakdown();
          }

          return throwError(() => error);
        })
      );
  }

  listTopUpHistory(limit?: number): Observable<TopUpHistoryEntry[]> {
    if (this.fellBackToMock) {
      return this.requireMockService('listTopUpHistory', 'T5 fallback').listTopUpHistory(limit);
    }

    return this.http
      .post<FunctionResponse<ApiTopUpHistoryListResponse>>(
        `${this.apiBaseUrl}/api/functions/billing/credits/history/list/execute`,
        this.buildFunctionRequest({ Limit: limit ?? null })
      )
      .pipe(
        map(response => response.data.Entries.map(entry => this.mapTopUpHistoryEntry(entry))),
        catchError(error => {
          if (this.shouldFallbackToMock(error)) {
            this.fellBackToMock = true;
            this.disconnectPaymentRecoveryStream();
            this.ensureMockBridge();
            return this.requireMockService('listTopUpHistory', 'T5 fallback').listTopUpHistory(limit);
          }

          return throwError(() => error);
        })
      );
  }

  previewPlanChange(_input: PlanChangeInput): Observable<PlanChangePreview | { error: PlanChangeError }> {
    return this.http
      .post<FunctionResponse<ApiPlanChangePreview>>(
        `${this.apiBaseUrl}/api/functions/billing/plan/preview/execute`,
        {
          parameters: {
            PlanCode: _input.targetTier,
            BillingPeriod: _input.targetBillingPeriod,
            UserCount: _input.targetSeats
          },
          pagination: { pageNumber: 1, pageSize: 1 },
          sorting: { sortField: '', sortDirection: 'asc' }
        }
      )
      .pipe(
        map(response => this.mapPlanChangePreview(response.data)),
        catchError(error => {
          this.logger.warn('BillingService', 'previewPlanChange', 'Plan-change preview failed', { error });
          return of({ error: this.mapPlanChangeError(error) });
        })
      );
  }

  commitPlanChange(
    _input: PlanChangeInput,
    _previewToken: string
  ): Observable<Subscription | { error: PlanChangeError }> {
    return this.http
      .post<FunctionResponse<ApiSubscription>>(
        `${this.apiBaseUrl}/api/functions/billing/plan/update/execute`,
        {
          parameters: {
            PlanCode: _input.targetTier,
            BillingPeriod: _input.targetBillingPeriod,
            UserCount: _input.targetSeats,
            PreviewToken: _previewToken
          },
          pagination: { pageNumber: 1, pageSize: 1 },
          sorting: { sortField: '', sortDirection: 'asc' }
        }
      )
      .pipe(
        map(response => this.mapSubscription(response.data)),
        switchMap(subscription => {
          if (subscription === null) {
            return of({ error: 'PREVIEW_STALE' as PlanChangeError });
          }

          this.subscriptionSubject.next(subscription);

          return this.refresh().pipe(
            map(() => subscription),
            catchError(error => {
              this.logger.error('BillingService', 'commitPlanChange', 'Refresh after plan change failed', { error });
              return of(subscription);
            })
          );
        }),
        catchError(error => {
          this.logger.warn('BillingService', 'commitPlanChange', 'Plan-change commit failed', { error });
          return of({ error: this.mapPlanChangeError(error) });
        })
      );
  }

  previewCancellation(
    reason: CancellationReason,
    freeText?: string
  ): Observable<CancellationPreview | { error: CancellationError }> {
    return this.http
      .post<FunctionResponse<ApiCancellationPreview>>(
        `${this.apiBaseUrl}/api/functions/billing/cancel/preview/execute`,
        this.buildFunctionRequest({
          Reason: reason,
          FreeTextFeedback: freeText?.trim() || null
        })
      )
      .pipe(
        map(response => this.mapCancellationPreview(response.data)),
        catchError((err: HttpErrorResponse) =>
          this.toTypedError<CancellationError>(err, [
            'CANCEL_INVALID_REASON',
            'CANCEL_NO_SUBSCRIPTION',
            'CANCEL_ALREADY_CANCELLED'
          ])
        )
      );
  }

  commitCancellation(input: CancellationInput): Observable<Subscription | { error: CancellationError }> {
    return this.postSubscriptionMutation<CancellationError>(
      '/api/functions/billing/cancel/commit/execute',
      {
        Reason: input.reason,
        FreeTextFeedback: input.freeText,
        AcceptedRetentionOfferId: input.acceptedRetentionOfferId ?? null,
        OfferedRetentionOffers: (input.offeredRetentionOffers ?? []).map(offer => this.toApiRetentionOffer(offer))
      },
      ['CANCEL_INVALID_REASON', 'CANCEL_NO_SUBSCRIPTION', 'CANCEL_ALREADY_CANCELLED']
    );
  }

  reactivate(): Observable<Subscription | { error: ReactivateError }> {
    return this.postSubscriptionMutation<ReactivateError>(
      '/api/functions/billing/reactivate/execute',
      {},
      [
        'GRACE_EXPIRED',
        'NOT_CANCELLED',
        'PAYMENT_METHOD_INVALID',
        'REACTIVATE_GRACE_PERIOD_EXPIRED',
        'REACTIVATE_NOT_CANCELLED',
        'REACTIVATE_PAYMENT_METHOD_INVALID'
      ]
    );
  }

  acceptRetentionOffer(
    offerType: RetentionOfferType,
    payload: Record<string, unknown>
  ): Observable<Subscription | { error: RetentionOfferError }>;
  acceptRetentionOffer(
    offerId: string,
    offerDetails: RetentionOffer
  ): Observable<Subscription | { error: RetentionOfferError }>;
  acceptRetentionOffer(
    offerOrId: string,
    payloadOrOffer: Record<string, unknown> | RetentionOffer
  ): Observable<Subscription | { error: RetentionOfferError }> {
    if (!this.hasRetentionOfferId(offerOrId) || !this.isRetentionOffer(payloadOrOffer)) {
      return this.notImplemented$('acceptRetentionOffer', 'T3 - use offerId + offerDetails');
    }

    return this.postSubscriptionMutation<RetentionOfferError>(
      '/api/functions/billing/retention/accept/execute',
      {
        RetentionOfferId: offerOrId,
        OfferDetails: this.toApiRetentionOffer(payloadOrOffer)
      },
      [
        'RETENTION_OFFER_RATE_LIMITED',
        'RETENTION_OFFER_EXPIRED',
        'RETENTION_OFFER_INVALID',
        'RETENTION_DOWNGRADE_PENDING_T2'
      ]
    );
  }

  getEnforcementStatus(): Observable<EnforcementStatus> {
    return defer(() => {
      if (!this.hasAuthToken()) {
        return of(this.emptyEnforcementStatus());
      }

      return this.ensureEnforcementLoaded().pipe(
        switchMap(() => this.enforcementStatusSubject.asObservable())
      );
    });
  }

  getCurrentUser(): Observable<CurrentUser> {
    return defer(() => {
      if (!this.hasAuthToken()) {
        return of(this.emptyCurrentUser());
      }

      return this.whenAccountViewAllowed().pipe(
        switchMap(allowed => {
          if (!allowed) {
            const staff = this.staffCurrentUser();
            this.currentUserSubject.next(staff);
            this.hasLoadedCurrentUser = true;
            return of(staff);
          }

          return this.ensureCurrentUserLoaded().pipe(
            switchMap(() => this.currentUserSubject.asObservable())
          );
        })
      );
    });
  }

  previewTax(input: TaxPreviewInput): Observable<TaxPreview> {
    return this.http
      .post<FunctionResponse<ApiTaxPreview>>(
        `${this.apiBaseUrl}/api/functions/billing/tax/preview/execute`,
        this.buildFunctionRequest({
          CountryCode: input.countryCode,
          Currency: input.currency,
          TaxableAmountMinor: input.taxableAmountMinor,
          ProductCategory: input.productCategory ?? null,
          CustomerTaxId: input.customerTaxId
            ? {
                CountryCode: input.customerTaxId.countryCode,
                Type: input.customerTaxId.type,
                Value: input.customerTaxId.value
              }
            : null
        })
      )
      .pipe(map(response => this.mapTaxPreview(response.data)));
  }

  listTaxIds(): Observable<TaxIdRecord[]> {
    return this.http
      .post<FunctionResponse<ApiTaxIdRecord[]>>(
        `${this.apiBaseUrl}/api/functions/billing/tax-ids/list/execute`,
        this.buildFunctionRequest({})
      )
      .pipe(map(response => response.data.map(record => this.mapTaxIdRecord(record))));
  }

  addTaxId(input: TaxIdInput): Observable<TaxIdRecord | { error: TaxIdError }> {
    return this.http
      .post<FunctionResponse<ApiTaxIdRecord>>(
        `${this.apiBaseUrl}/api/functions/billing/tax-ids/add/execute`,
        this.buildFunctionRequest({
          CountryCode: input.countryCode,
          Type: input.type,
          Value: input.value
        })
      )
      .pipe(
        map(response => this.mapTaxIdRecord(response.data)),
        switchMap(record => this.refresh().pipe(map(() => record))),
        catchError((err: HttpErrorResponse) => of({ error: this.mapTaxIdError(err) }))
      );
  }

  removeTaxId(taxIdRecordId: string): Observable<void> {
    return this.http
      .post<FunctionResponse<Record<string, never>>>(
        `${this.apiBaseUrl}/api/functions/billing/tax-ids/remove/execute`,
        this.buildFunctionRequest({ TenantTaxIdId: taxIdRecordId })
      )
      .pipe(switchMap(() => this.refresh()));
  }

  __setScenario(_name: ScenarioName): void {
    if (environment.production) {
      return;
    }

    const mock = this.activateMockFallback('__setScenario', 'T0.3 dev');
    mock.__setScenario(_name);
  }

  __getActiveScenario(): ScenarioName {
    const mock = this.tryGetMockService();
    return mock?.__getActiveScenario() ?? 'healthy_monthly_us';
  }

  __simulateStalePreview(_active: boolean): void {
    if (environment.production) {
      return;
    }

    const mock = this.activateMockFallback('__simulateStalePreview', 'T0.3 dev');
    mock.__simulateStalePreview(_active);
  }

  __simulateStalePreviewWithDiff(_active: boolean): void {
    if (environment.production) {
      return;
    }

    const mock = this.activateMockFallback('__simulateStalePreviewWithDiff', 'T0.3 dev');
    mock.__simulateStalePreviewWithDiff(_active);
  }

  __simulateCardDecline(_active: boolean): void {
    if (environment.production) {
      return;
    }

    const mock = this.activateMockFallback('__simulateCardDecline', 'T0.3 dev');
    mock.__simulateCardDecline(_active);
  }

  __resetRetentionRateLimit(): void {
    if (environment.production) {
      return;
    }

    const mock = this.activateMockFallback('__resetRetentionRateLimit', 'T0.3 dev');
    mock.__resetRetentionRateLimit();
  }

  __simulatePaymentFailure(): void {
    if (environment.production) {
      return;
    }

    const mock = this.activateMockFallback('__simulatePaymentFailure', 'T0.3 dev');
    mock.__simulatePaymentFailure();
  }

  __simulatePaymentRecovery(): void {
    if (environment.production) {
      return;
    }

    const mock = this.activateMockFallback('__simulatePaymentRecovery', 'T0.3 dev');
    mock.__simulatePaymentRecovery();
  }

  __setUserRole(_role: UserRole): void {
    if (environment.production) {
      return;
    }

    const mock = this.activateMockFallback('__setUserRole', 'T0.3 dev');
    mock.__setUserRole(_role);
  }

  __simulateAIConsumption(_amountCredits: number): void {
    if (environment.production) {
      return;
    }

    const mock = this.activateMockFallback('__simulateAIConsumption', 'T0.3 dev');
    mock.__simulateAIConsumption(_amountCredits);
  }

  __resetAICreditCycle(): void {
    if (environment.production) {
      return;
    }

    const mock = this.activateMockFallback('__resetAICreditCycle', 'T0.3 dev');
    mock.__resetAICreditCycle();
  }

  __simulateTopUpFailure(_active: boolean): void {
    if (environment.production) {
      return;
    }

    const mock = this.activateMockFallback('__simulateTopUpFailure', 'T0.3 dev');
    mock.__simulateTopUpFailure(_active);
  }

  private withSummary<T>(stream$: Observable<T>): Observable<T> {
    return defer(() => this.ensureSummaryLoaded().pipe(switchMap(() => stream$)));
  }

  private ensureSummaryLoaded(): Observable<void> {
    if (!this.hasAuthToken()) {
      return of(void 0);
    }

    if (this.fellBackToMock) {
      return this.populateFromMock();
    }

    if (this.hasLoadedSummary) {
      return of(void 0);
    }

    return this.refresh();
  }

  private get apiBaseUrl(): string {
    try {
      return this.appSettings.apiUrl;
    } catch {
      return environment.apiUrl;
    }
  }

  private ensureEnforcementLoaded(): Observable<void> {
    if (!this.hasAuthToken()) {
      return of(void 0);
    }

    if (this.fellBackToMock) {
      return this.populateEnforcementFromMock();
    }

    if (this.hasLoadedEnforcementStatus) {
      return of(void 0);
    }

    if (this.inFlightRefresh$) {
      return this.inFlightRefresh$;
    }

    return this.refreshEnforcementStatus();
  }

  private ensureCurrentUserLoaded(): Observable<void> {
    if (!this.hasAuthToken()) {
      return of(void 0);
    }

    if (this.fellBackToMock) {
      return this.populateCurrentUserFromMock();
    }

    if (this.hasLoadedCurrentUser) {
      return of(void 0);
    }

    return this.refreshCurrentUser();
  }

  private refreshEnforcementStatus(): Observable<void> {
    if (this.fellBackToMock) {
      return this.populateEnforcementFromMock();
    }

    if (this.inFlightRefresh$) {
      return this.inFlightRefresh$;
    }

    if (this.inFlightEnforcementRefresh$) {
      return this.inFlightEnforcementRefresh$;
    }

    this.inFlightEnforcementRefresh$ = this.fetchEnforcementStatusData().pipe(
      tap(status => {
        this.enforcementStatusSubject.next(this.mapEnforcementStatus(status));
        this.hasLoadedEnforcementStatus = true;
      }),
      map(() => void 0),
      catchError(error => {
        if (this.shouldFallbackToMock(error)) {
          this.fellBackToMock = true;
          this.disconnectPaymentRecoveryStream();
          this.ensureMockBridge();
          return this.populateEnforcementFromMock();
        }

        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.logger.warn(
            'BillingService',
            'refreshEnforcementStatus',
            'Enforcement status unauthorized; using empty billing context',
            { error }
          );
          this.enforcementStatusSubject.next(this.emptyEnforcementStatus());
          this.hasLoadedEnforcementStatus = true;
          return of(void 0);
        }

        if (error instanceof HttpErrorResponse && error.status === 403) {
          this.logger.warn(
            'BillingService',
            'refreshEnforcementStatus',
            'Enforcement status forbidden; using empty billing context',
            { error }
          );
          this.enforcementStatusSubject.next(this.emptyEnforcementStatus());
          this.hasLoadedEnforcementStatus = true;
          return of(void 0);
        }

        this.logger.error('BillingService', 'refreshEnforcementStatus', 'Enforcement status request failed', { error });
        return throwError(() => error);
      }),
      finalize(() => {
        this.inFlightEnforcementRefresh$ = null;
      }),
      shareReplay(1)
    );

    return this.inFlightEnforcementRefresh$;
  }

  private refreshCurrentUser(): Observable<void> {
    if (this.fellBackToMock) {
      return this.populateCurrentUserFromMock();
    }

    if (this.inFlightCurrentUser$) {
      return this.inFlightCurrentUser$;
    }

    this.inFlightCurrentUser$ = this.fetchCurrentUserData().pipe(
      tap(user => {
        this.currentUserSubject.next(this.mapCurrentUser(user));
        this.hasLoadedCurrentUser = true;
      }),
      map(() => void 0),
      catchError(error => {
        if (this.shouldFallbackToMock(error)) {
          this.fellBackToMock = true;
          this.disconnectPaymentRecoveryStream();
          this.ensureMockBridge();
          return this.populateCurrentUserFromMock();
        }

        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.logger.warn(
            'BillingService',
            'refreshCurrentUser',
            'Current user request unauthorized; using empty billing context',
            { error }
          );
          this.currentUserSubject.next(this.emptyCurrentUser());
          this.hasLoadedCurrentUser = true;
          return of(void 0);
        }

        this.logger.error('BillingService', 'refreshCurrentUser', 'Current user request failed', { error });
        return throwError(() => error);
      }),
      finalize(() => {
        this.inFlightCurrentUser$ = null;
      }),
      shareReplay(1)
    );

    return this.inFlightCurrentUser$;
  }

  private fetchSummaryData(): Observable<BillingSummaryApiResponse> {
    return this.http
      .post<FunctionResponse<BillingSummaryApiResponse>>(
        `${this.apiBaseUrl}/api/functions/billing/subscription/execute`,
        this.buildFunctionRequest({})
      )
      .pipe(
        map(response => response.data)
      );
  }

  private fetchEnforcementStatusData(): Observable<EnforcementStatusApiResponse> {
    return this.http
      .post<FunctionResponse<EnforcementStatusApiResponse>>(
        `${this.apiBaseUrl}/api/functions/billing/enforcement/status/execute`,
        this.buildFunctionRequest({})
      )
      .pipe(
        map(response => response.data)
      );
  }

  private fetchCurrentUserData(): Observable<CurrentUserApiResponse> {
    return this.http
      .post<FunctionResponse<CurrentUserApiResponse>>(
        `${this.apiBaseUrl}/api/functions/billing/user/me/execute`,
        this.buildFunctionRequest({})
      )
      .pipe(
        map(response => response.data)
      );
  }

  private populateFromMock(): Observable<void> {
    const mock = this.requireMockService('populateFromMock', 'T0.3 fallback');

    return mock.getCurrentSubscription().pipe(
      switchMap(subscription => {
        const country = subscription?.countryCode ?? 'US';
        const currency = subscription?.currency ?? 'USD';

        return forkJoin({
          subscription: of(subscription),
          plans: mock.getAvailablePlans(country, currency),
          aiCreditBalance: mock.getAICreditBalance()
        });
      }),
      tap(result => {
        this.subscriptionSubject.next(result.subscription);
        this.availablePlansSubject.next(result.plans);
        this.aiCreditBalanceSubject.next(result.aiCreditBalance);
        this.hasLoadedSummary = true;
      }),
      map(() => void 0)
    );
  }

  private applyBillingSummary(summary: BillingSummaryApiResponse): void {
    const mappedSubscription = this.mapSubscription(summary.Subscription);
    const mappedPlans = this.mapPlans(summary.AvailablePlans);
    const mappedBalance = this.mapAICreditBalance(summary.AICreditBalance);

    this.subscriptionSubject.next(mappedSubscription);
    this.availablePlansSubject.next(mappedPlans);
    this.aiCreditBalanceSubject.next(mappedBalance);
    this.hasLoadedSummary = true;
  }

  private mapCancellationPreview(api: ApiCancellationPreview): CancellationPreview {
    return {
      reason: api.Reason,
      endOfPeriod: api.EndOfPeriod,
      isAnnual: api.IsAnnual,
      isPastDue: api.IsPastDue,
      topUpBalanceAtCancel: api.TopUpBalanceAtCancel,
      cycleCreditsRemaining: api.CycleCreditsRemaining,
      offers: api.Offers.map(offer => this.mapRetentionOffer(offer)),
      retentionOffersRateLimited: api.RetentionOffersRateLimited,
      // Solo's cancel rule (ADO 19427); an older API that does not send it means today's behaviour.
      accessEnds: api.AccessEnds === 'Now' ? 'now' : 'period_end'
    };
  }

  private mapRetentionOffer(api: ApiRetentionOffer): RetentionOffer {
    return {
      retentionOfferId: api.RetentionOfferId,
      type: api.Type,
      headline: api.Headline,
      body: api.Body,
      ctaLabel: api.CtaLabel,
      payload: api.Payload ?? {}
    };
  }

  private populateEnforcementFromMock(): Observable<void> {
    const mock = this.requireMockService('getEnforcementStatus', 'T4 fallback');

    return mock.getEnforcementStatus().pipe(
      take(1),
      tap(status => {
        this.enforcementStatusSubject.next(status);
        this.hasLoadedEnforcementStatus = true;
      }),
      map(() => void 0)
    );
  }

  private populateCurrentUserFromMock(): Observable<void> {
    const mock = this.requireMockService('getCurrentUser', 'T4 fallback');

    return mock.getCurrentUser().pipe(
      take(1),
      tap(user => {
        this.currentUserSubject.next(user);
        this.hasLoadedCurrentUser = true;
      }),
      map(() => void 0)
    );
  }

  private mapSubscription(api: ApiSubscription | null): Subscription | null {
    if (!api) {
      return null;
    }

    return {
      id: api.Id,
      stripeSubscriptionId: api.StripeSubscriptionId,
      tenantId: api.TenantId,
      status: this.toSubscriptionStatus(api.Status),
      tier: this.toTierCode(api.Tier),
      billingPeriod: this.toBillingPeriod(api.BillingPeriod),
      currency: this.toCurrencyCode(api.Currency),
      countryCode: this.toCountryCode(api.CountryCode),
      seats: api.Seats,
      pricingModel: normalizePricingModel(api.PricingModel),
      includedSeats: api.IncludedSeats ?? null,
      extraSeatPriceMinor: api.ExtraSeatPriceMinor ?? null,
      pricePerSeatMinor: api.PricePerSeatMinor,
      subtotalMinor: api.SubtotalMinor,
      discountMinor: api.DiscountMinor,
      taxMinor: api.TaxMinor,
      totalMinor: api.TotalMinor,
      appliedCoupon: this.mapAppliedCoupon(api.AppliedCoupon),
      currentPeriodStart: api.CurrentPeriodStart,
      currentPeriodEnd: api.CurrentPeriodEnd ?? '',
      cancelAtPeriodEnd: api.CancelAtPeriodEnd,
      canceledAt: api.CanceledAt,
      pastDueSince: api.PastDueSince,
      gracePeriodEndsAt: api.GracePeriodEndsAt
    };
  }

  private mapAppliedCoupon(api: ApiAppliedCoupon | null): AppliedCoupon | null {
    if (!api) {
      return null;
    }

    return {
      promotionCode: api.PromotionCode,
      couponId: api.CouponId,
      name: api.Name,
      discountType: api.DiscountType,
      percentOff: api.PercentOff,
      amountOffMinor: api.AmountOffMinor,
      duration: api.Duration,
      durationInMonths: api.DurationInMonths,
      appliedAt: api.AppliedAt,
      expiresAt: api.ExpiresAt
    };
  }

  private mapPlans(apiPlans: ApiPlan[]): Plan[] {
    return apiPlans.map(plan => ({
      code: this.toTierCode(plan.Code),
      displayName: plan.DisplayName,
      shortDescription: plan.ShortDescription,
      features: plan.Features,
      pricing: plan.Pricing.map(price => ({
        currency: this.toCurrencyCode(price.Currency),
        countryCode: this.toCountryCode(price.CountryCode),
        billingPeriod: this.toBillingPeriod(price.BillingPeriod),
        perSeatMinor: price.PerSeatMinor,
        aiCreditsPerSeat: price.AICreditsPerSeat,
        basicTurnsPerSeat: price.BasicTurnsPerSeat,
        pricingModel: normalizePricingModel(price.PricingModel),
        includedSeats: price.IncludedSeats ?? null,
        extraSeatMonthlyMinor: price.ExtraSeatMonthlyMinor ?? null,
        stripePriceId: price.StripePriceId
      }))
    }));
  }

  private postSubscriptionMutation<TError extends string>(
    path: string,
    parameters: Record<string, unknown>,
    allowedCodes: readonly TError[]
  ): Observable<Subscription | { error: TError }> {
    return this.http
      .post<FunctionResponse<BillingSummaryApiResponse>>(
        `${this.apiBaseUrl}${path}`,
        this.buildFunctionRequest(parameters)
      )
      .pipe(
        map(response => {
          this.applyBillingSummary(response.data);
          const subscription = this.subscriptionSubject.value;
          if (subscription === null) {
            throw new Error(`BillingService mutation ${path} did not return a subscription.`);
          }
          return subscription;
        }),
        catchError((err: HttpErrorResponse) => this.toTypedError<TError>(err, allowedCodes))
      );
  }

  private toTypedError<TError extends string>(
    err: HttpErrorResponse,
    allowedCodes: readonly TError[]
  ): Observable<{ error: TError }> {
    const code = err.error?.messages?.[0]?.code as string | undefined;
    if (code && allowedCodes.includes(code as TError)) {
      return of({ error: code as TError });
    }

    return throwError(() => err);
  }

  private mapAICreditBalance(api: ApiAICreditBalance | null): AICreditBalance | null {
    if (!api) {
      return null;
    }

    return {
      tenantId: api.TenantId,
      billingCycleStart: api.BillingCycleStart,
      billingCycleEnd: api.BillingCycleEnd,
      allocatedCredits: api.AllocatedCredits,
      usedCredits: api.UsedCredits,
      reservedCredits: api.ReservedCredits,
      topUpBalance: api.TopUpBalance,
      remainingCredits: api.RemainingCredits,
      basicTurnsAllocated: api.BasicTurnsAllocated,
      basicTurnsUsed: api.BasicTurnsUsed
    };
  }

  private mapPlanChangePreview(api: ApiPlanChangePreview): PlanChangePreview {
    return {
      previewToken: api.PreviewToken,
      currentSubtotalMinor: api.CurrentSubtotalMinor,
      newSubtotalMinor: api.NewSubtotalMinor,
      prorationLines: api.ProrationLines.map(line => ({
        description: line.Description,
        periodStart: line.PeriodStart,
        periodEnd: line.PeriodEnd,
        amountMinor: line.AmountMinor
      })),
      subtotalMinor: api.SubtotalMinor,
      discountMinor: api.DiscountMinor,
      totalMinor: api.TotalMinor,
      accountCreditAppliedMinor: api.AccountCreditAppliedMinor,
      effectiveDate: api.EffectiveDate,
      // Solo's plan change rules (ADO 19427); an older API means today's immediate change.
      changeTiming: api.ChangeTiming === 'renewal' ? 'renewal' : 'immediate',
      nextRegularChargeAt: api.NextRegularChargeAt,
      nextRegularChargeMinor: api.NextRegularChargeMinor,
      currency: this.toCurrencyCode(api.Currency),
      warnings: api.Warnings.map(warning => this.mapPlanChangeWarning(warning)),
      resolvedInput: {
        targetTier: this.toTierCode(api.ResolvedInput.TargetTier),
        targetSeats: api.ResolvedInput.TargetSeats,
        targetBillingPeriod: this.toBillingPeriod(api.ResolvedInput.TargetBillingPeriod)
      }
    };
  }

  private mapTopUpPack(api: ApiTopUpPack, requestedCurrency: CurrencyCode): TopUpPack {
    return {
      id: api.Id,
      displayName: api.DisplayName,
      credits: api.Credits,
      pricing: api.Pricing
        .filter(price => this.toCurrencyCode(price.Currency) === requestedCurrency)
        .map(price => ({
          currency: this.toCurrencyCode(price.Currency),
          countryCode: this.toCountryCode(price.CountryCode),
          amountMinor: price.AmountMinor,
          stripePriceId: price.StripePriceId
        }))
    };
  }

  private mapTopUpHistoryEntry(api: ApiTopUpHistoryEntry): TopUpHistoryEntry {
    return {
      id: api.Id,
      creditTopUpId: api.CreditTopUpId,
      purchasedAt: api.PurchasedAt,
      packDisplayName: api.PackDisplayName,
      credits: api.Credits,
      amountMinor: api.AmountMinor,
      currency: this.toCurrencyCode(api.Currency),
      remaining: api.Remaining,
      status: this.toTopUpStatus(api.Status)
    };
  }

  private mapConsumptionEvent(api: ApiConsumptionEvent): ConsumptionEvent {
    return {
      id: api.Id,
      occurredAt: api.OccurredAt,
      featureKey: this.toFeatureKey(api.FeatureKey),
      creditsConsumed: api.CreditsConsumed,
      modelTier: api.ModelTier === 'basic' ? 'basic' : 'premium',
      requestSummary: api.RequestSummary,
      balanceAfter: api.BalanceAfter
    };
  }

  private mapFeatureUsageBreakdown(api: ApiFeatureUsageBreakdown): FeatureUsageBreakdown {
    return {
      featureKey: this.toFeatureKey(api.FeatureKey),
      displayName: api.DisplayName,
      creditsUsed: api.CreditsUsed,
      requestCount: api.RequestCount,
      percentOfTotal: api.PercentOfTotal
    };
  }

  private mapPlanChangeWarning(api: ApiPlanChangeWarning) {
    switch (api.Code) {
      case 'USAGE_EXCEEDS_NEW_ALLOCATION':
        return {
          code: 'USAGE_EXCEEDS_NEW_ALLOCATION' as const,
          usedCredits: api.UsedCredits ?? 0,
          newAllocation: api.NewAllocation ?? 0
        };
      case 'SEATS_BELOW_ACTIVE_USERS':
        return {
          code: 'SEATS_BELOW_ACTIVE_USERS' as const,
          activeUsers: api.ActiveUsers ?? 0,
          newSeats: api.NewSeats ?? 0
        };
      case 'SWITCHING_TO_ANNUAL':
        return {
          code: 'SWITCHING_TO_ANNUAL' as const,
          firstAnnualChargeMinor: api.FirstAnnualChargeMinor ?? 0,
          nextRenewalAt: api.NextRenewalAt ?? ''
        };
      case 'SWITCHING_TO_MONTHLY':
      default:
        return {
          code: 'SWITCHING_TO_MONTHLY' as const,
          accountCreditAppliedMinor: api.AccountCreditAppliedMinor ?? 0
        };
    }
  }

  private mapPlanChangeError(error: unknown): PlanChangeError {
    const code = this.extractFunctionErrorCode(error);
    switch (code) {
      case 'NO_OP':
        return 'NO_OP';
      case 'STRIPE_CARD_DECLINED':
      case 'CARD_DECLINED':
        return 'CARD_DECLINED';
      case 'SEATS_BELOW_ACTIVE_USERS':
      case 'PREVIEW_STALE':
      default:
        return 'PREVIEW_STALE';
    }
  }

  private extractFunctionErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const body = error.error as { messages?: Array<{ code?: string }> } | null | undefined;
    return body?.messages?.[0]?.code ?? null;
  }

  private mapEnforcementStatus(api: EnforcementStatusApiResponse): EnforcementStatus {
    const severity = this.mapEnforcementSeverity(api.Severity ?? undefined);

    return {
      level: api.Level,
      ...(severity ? { severity } : {}),
      ...(api.DaysRemaining !== null && api.DaysRemaining !== undefined
        ? { daysRemaining: api.DaysRemaining }
        : {}),
      ...(api.PastDueSince ? { pastDueSince: api.PastDueSince } : {}),
      ...(api.GracePeriodEndsAt ? { gracePeriodEndsAt: api.GracePeriodEndsAt } : {}),
      restrictedFeatures: api.RestrictedFeatures ?? [],
      ctaUrl: api.CtaUrl || '/billing'
    };
  }

  private mapEnforcementSeverity(value?: string): 'yellow' | 'orange' | 'red' | undefined {
    switch (value) {
      case 'info':
        return 'yellow';
      case 'warning':
        return 'orange';
      case 'urgent':
      case 'blocked':
        return 'red';
      default:
        return undefined;
    }
  }

  private mapCurrentUser(api: CurrentUserApiResponse): CurrentUser {
    return {
      id: api.Id,
      canManageBilling: api.CanManageBilling
    };
  }

  private mapTaxPreview(api: ApiTaxPreview): TaxPreview {
    return {
      taxableAmountMinor: api.TaxableAmountMinor,
      taxAmountMinor: api.TaxAmountMinor,
      taxRate: api.TaxRate,
      taxBasis: api.TaxBasis,
      jurisdiction: api.Jurisdiction,
      label: api.Label,
      calculation: api.Calculation,
      ...(api.ReverseChargeNote ? { reverseChargeNote: api.ReverseChargeNote } : {})
    };
  }

  private mapTaxIdRecord(api: ApiTaxIdRecord): TaxIdRecord {
    return {
      id: api.Id,
      type: api.Type,
      value: api.Value,
      countryCode: this.toCountryCode(api.CountryCode),
      valid: api.Valid ?? api.VerificationStatus !== 'unverified',
      ...(api.ValidationError ? { validationError: api.ValidationError } : {}),
      createdAt: api.CreatedAt ?? api.AddedOn ?? new Date().toISOString()
    };
  }

  private mapTaxIdError(error: HttpErrorResponse): TaxIdError {
    const code = this.extractFunctionErrorCode(error);
    return code === 'UNSUPPORTED_COUNTRY' ? 'UNSUPPORTED_COUNTRY' : 'INVALID_FORMAT';
  }

  private hasAuthToken(): boolean {
    return !!this.authService.getAuthToken();
  }

  private emptyEnforcementStatus(): EnforcementStatus {
    return {
      level: 'none',
      restrictedFeatures: [],
      ctaUrl: '/billing'
    };
  }

  private emptyCurrentUser(): CurrentUser {
    return {
      id: '',
      canManageBilling: true
    };
  }

  private applyEmptyBillingContext(): void {
    this.subscriptionSubject.next(null);
    this.availablePlansSubject.next([]);
    this.aiCreditBalanceSubject.next(null);
    this.enforcementStatusSubject.next(this.emptyEnforcementStatus());
    this.currentUserSubject.next(this.emptyCurrentUser());
  }

  private resetSessionCache(): void {
    this.hasLoadedSummary = false;
    this.hasLoadedEnforcementStatus = false;
    this.hasLoadedCurrentUser = false;
    this.inFlightRefresh$ = null;
    this.inFlightEnforcementRefresh$ = null;
    this.inFlightCurrentUser$ = null;
    this.accountViewGeneration += 1;
    this.accountViewAllowed = null;
    this.accountViewLookup = null;
    this.applyEmptyBillingContext();
  }

  private whenAccountViewAllowed(): Observable<boolean> {
    return defer(() =>
      this.accountViewAllowed === null ? from(this.lookupAccountView()) : of(this.accountViewAllowed)
    );
  }

  private lookupAccountView(): Promise<boolean> {
    if (this.accountViewAllowed !== null) {
      return Promise.resolve(this.accountViewAllowed);
    }

    if (!this.accountViewLookup) {
      const generation = this.accountViewGeneration;
      this.accountViewLookup = this.permissions
        .has(BillingPermissions.AccountView)
        .then(allowed => {
          if (generation !== this.accountViewGeneration) {
            return this.lookupAccountView();
          }

          this.accountViewAllowed = allowed;
          return allowed;
        })
        .catch(() => {
          if (generation !== this.accountViewGeneration) {
            return this.lookupAccountView();
          }

          this.accountViewAllowed = false;
          return false;
        })
        .finally(() => {
          if (generation === this.accountViewGeneration) {
            this.accountViewLookup = null;
          }
        });
    }

    return this.accountViewLookup;
  }

  private skipAccountBilling(): Observable<void> {
    this.subscriptionSubject.next(null);
    this.availablePlansSubject.next([]);
    this.aiCreditBalanceSubject.next(null);
    this.hasLoadedSummary = true;
    return of(void 0);
  }

  private enforcementForAccountRefresh(): Observable<EnforcementStatusApiResponse | null> {
    if (this.hasLoadedEnforcementStatus) {
      return of(null);
    }

    if (this.inFlightEnforcementRefresh$) {
      return this.inFlightEnforcementRefresh$.pipe(map(() => null));
    }

    return this.fetchEnforcementStatusData().pipe(
      catchError(error => {
        if (!(error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403))) {
          return throwError(() => error);
        }

        this.logger.warn(
          'BillingService',
          'enforcementForAccountRefresh',
          'Enforcement status request denied; using empty enforcement status',
          { error }
        );
        this.enforcementStatusSubject.next(this.emptyEnforcementStatus());
        return of(null);
      })
    );
  }

  private staffCurrentUser(): CurrentUser {
    return {
      id: '',
      canManageBilling: false
    };
  }

  private shouldFallbackToMock(error: unknown): boolean {
    return !environment.production && error instanceof HttpErrorResponse && error.status === 0;
  }

  private toCurrencyCode(value: string): CurrencyCode {
    return this.inList(value, BILLING_CURRENCY_CODES, 'USD');
  }

  private toCountryCode(value: string): CountryCode {
    return this.inList(value, COUNTRY_CODES, 'US');
  }

  private toBillingPeriod(value: string): 'monthly' | 'annual' {
    return this.inList(value, BILLING_PERIODS, 'monthly');
  }

  private toTierCode(value: string): 'FOUNDATION' | 'PROFESSIONAL' | 'BUSINESS' {
    return this.inList(value, TIER_CODES, 'FOUNDATION');
  }

  private toSubscriptionStatus(
    value: string
  ): 'active' | 'past_due' | 'unpaid' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'paused' {
    return this.inList(value, SUBSCRIPTION_STATUSES, 'active');
  }

  private toFeatureKey(value: string): ConsumptionEvent['featureKey'] {
    return this.inList(
      value,
      ['ai_chat', 'document_extraction', 'image_generation', 'voice_transcription', 'summarization', 'other'] as const,
      'other'
    );
  }

  private toTopUpStatus(value: string): TopUpHistoryEntry['status'] {
    return this.inList(value, ['initiated', 'completed', 'failed', 'expired'] as const, 'initiated');
  }

  private inList<T extends string>(value: string, allowed: readonly T[], fallback: T): T {
    const normalized = value?.trim();
    return allowed.includes(normalized as T) ? (normalized as T) : fallback;
  }

  private ensureMockBridge(): void {
    if (this.mockBridgeInitialized) {
      return;
    }

    const mock = this.requireMockService('ensureMockBridge', 'T4 fallback');
    this.mockBridgeInitialized = true;

    mock.getEnforcementStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.enforcementStatusSubject.next(status);
        this.hasLoadedEnforcementStatus = true;
      });

    mock.getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUserSubject.next(user);
        this.hasLoadedCurrentUser = true;
      });

    mock.paymentRecoveryEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        void firstValueFrom(this.populateFromMock()).catch(error => {
          this.logger.error('BillingService', 'ensureMockBridge', 'Failed to refresh billing summary after mock recovery event', { error });
        });
        this.paymentRecoverySubject.next();
      });
  }

  private connectPaymentRecoveryStream(token: string | null): void {
    if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
      return;
    }

    if (!token) {
      this.disconnectPaymentRecoveryStream();
      return;
    }

    if (this.paymentRecoveryEventSource !== null && this.paymentRecoveryEventToken === token) {
      return;
    }

    this.disconnectPaymentRecoveryStream();

    const streamUrl = `${environment.apiUrl}/api/billing/recovery-events?access_token=${encodeURIComponent(token)}`;
    const eventSource = new window.EventSource(streamUrl);
    this.paymentRecoveryEventSource = eventSource;
    this.paymentRecoveryEventToken = token;

    eventSource.onmessage = () => {
      this.paymentRecoverySubject.next();
      void firstValueFrom(this.refresh()).catch(error => {
        this.logger.error('BillingService', 'connectPaymentRecoveryStream', 'Failed to refresh after recovery event', { error });
      });
    };

    eventSource.onerror = (error: Event) => {
      this.logger.error('BillingService', 'connectPaymentRecoveryStream', 'Billing recovery events stream error', { error });
    };
  }

  private disconnectPaymentRecoveryStream(): void {
    this.paymentRecoveryEventSource?.close();
    this.paymentRecoveryEventSource = null;
    this.paymentRecoveryEventToken = null;
  }

  private tryGetMockService(): BillingMockService | null {
    return this.injector.get(BillingMockService, this.billingMockService ?? null);
  }

  private activateMockFallback(method: string, wave: string): BillingMockService {
    const mock = this.requireMockService(method, wave);
    if (!this.fellBackToMock) {
      this.fellBackToMock = true;
      this.disconnectPaymentRecoveryStream();
    }

    this.ensureMockBridge();
    return mock;
  }

  private requireMockService(method: string, wave: string): BillingMockService {
    const mock = this.tryGetMockService();
    if (mock) {
      return mock;
    }

    throw this.notImplementedError(method, wave);
  }

  private notImplemented$<T>(method: string, wave: string): Observable<T> {
    return throwError(() => this.notImplementedError(method, wave));
  }

  private notImplementedError(method: string, wave: string): Error {
    return new Error(`BillingService.${method}: not implemented — wired by ${wave}`);
  }

  private buildFunctionRequest(parameters: Record<string, unknown>): {
    parameters: Record<string, unknown>;
    pagination: { pageNumber: number; pageSize: number };
    sorting: { sortField: string; sortDirection: string };
  } {
    return {
      parameters,
      pagination: { pageNumber: 1, pageSize: 1 },
      sorting: { sortField: '', sortDirection: 'asc' }
    };
  }

  private toApiRetentionOffer(offer: RetentionOffer): Record<string, unknown> {
    return {
      RetentionOfferId: offer.retentionOfferId ?? null,
      Type: offer.type,
      Headline: offer.headline,
      Body: offer.body,
      CtaLabel: offer.ctaLabel,
      Payload: offer.payload
    };
  }

  private hasRetentionOfferId(value: string): boolean {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private isRetentionOffer(value: Record<string, unknown> | RetentionOffer): value is RetentionOffer {
    return typeof value === 'object' && value !== null && 'headline' in value && 'payload' in value;
  }

  private toAbsoluteUrl(url: string): string {
    try {
      return new URL(url, window.location.origin).toString();
    } catch {
      return url;
    }
  }

  private currentOrigin(): string {
    if (typeof window === 'undefined') {
      return this.apiBaseUrl;
    }

    return window.location.origin;
  }

  private storePendingCreditTopUpId(creditTopUpId: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.setItem('solo_pending_credit_top_up_id', creditTopUpId);
  }

  private mapTopUpCheckoutError(error: unknown): TopUpCheckoutError {
    const code = this.extractFunctionErrorCode(error);
    switch (code) {
      case 'PACK_NOT_FOUND':
        return 'PACK_NOT_FOUND';
      case 'PRICE_NOT_AVAILABLE_FOR_CURRENCY':
        return 'PRICE_NOT_AVAILABLE_FOR_CURRENCY';
      case 'CHECKOUT_SESSION_INVALID':
        return 'CHECKOUT_SESSION_INVALID';
      case 'STRIPE_DECLINED':
        return 'STRIPE_DECLINED';
      default:
        return 'NETWORK_ERROR';
    }
  }
}
